import { useState, type ReactNode } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";

interface CollapsibleCardProps {
  /** Header title (shown next to optional icon). */
  title: string;
  /** Optional numeric badge after the title. */
  count?: number;
  /** Optional icon node rendered before the title. */
  icon?: ReactNode;
  /** Optional extra header content rendered after the count (e.g. extra badges, totals). */
  headerExtra?: ReactNode;
  /** Whether the card starts open. Defaults to `true`. */
  defaultOpen?: boolean;
  /**
   * Optional action node rendered on the right side, outside the trigger.
   * Pass a function to conditionally render based on open state (e.g. hide
   * action buttons when the card is collapsed).
   */
  actions?: ReactNode | ((open: boolean) => ReactNode);
  /** Optional class for the outer card wrapper. */
  className?: string;
  /**
   * Optional class for the inner content wrapper. Defaults to `border-t p-4`
   * which matches most usages. Pass an explicit class to override (e.g.
   * `border-t-[1.5px] ...` for grid-style cards, or `border-t` only when you
   * want no padding).
   */
  contentClassName?: string;
  /** Card body. */
  children: ReactNode;
}

/**
 * Shared collapsible card used across project detail tabs.
 *
 * Replaces ~9 duplicate `border-[1.5px] border-foreground bg-card` + manual
 * collapse-button patterns. Uses Radix Collapsible under the hood.
 */
export function CollapsibleCard({
  title,
  count,
  icon,
  headerExtra,
  defaultOpen = true,
  actions,
  className,
  contentClassName,
  children,
}: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn("border-[1.5px] border-foreground bg-card", className)}
    >
      <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors rounded-t-xl">
        <CollapsibleTrigger className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {icon}
          <h3 className="font-display text-lg">{title}</h3>
          {count !== undefined && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
              {count}
            </span>
          )}
          {headerExtra}
        </CollapsibleTrigger>
        <div className="flex items-center gap-2">
          {typeof actions === "function" ? actions(open) : actions}
          <CollapsibleTrigger className="p-1" aria-label={open ? "Lukk seksjon" : "Åpne seksjon"}>
            {open ? (
              <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground" />
            ) : (
              <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground" />
            )}
          </CollapsibleTrigger>
        </div>
      </div>
      <CollapsibleContent className={cn(contentClassName ?? "border-t p-4")}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
